// Which day type applies on a date, and how blocks of a day are shown.
// Pure functions: the server uses them to generate days, the client to show a day offline (spec L35).

import { isoWeekday, timeToMinutes } from "./time";

export type SlotKind =
  "routine" | "sport" | "care" | "work" | "free" | "flex" | "meal" | "social" | "planning" | "chores" | "prep" | "rest" | "sleep";

export interface TemplateLike {
  id: string;
  is_fallback: boolean;
  archived_at: string | null;
  deleted_at: string | null;
}

export interface RuleLike {
  template_id: string;
  weekdays: number[];
  valid_from: string | null;
  valid_to: string | null;
  priority: number;
  deleted_at: string | null;
}

export interface OverrideLike {
  template_id: string;
  starts_on: string;
  ends_on: string;
  deleted_at: string | null;
}

/** Override covering the date, else the highest priority rule for its weekday, else the fallback type. */
export function pickTemplateId(date: string, templates: TemplateLike[], rules: RuleLike[], overrides: OverrideLike[]): string | null {
  const live = new Set(templates.filter((t) => !t.deleted_at && !t.archived_at).map((t) => t.id));
  const override = overrides.find((o) => !o.deleted_at && o.starts_on <= date && o.ends_on >= date && live.has(o.template_id));
  if (override) return override.template_id;
  const weekday = isoWeekday(date);
  const rule = rules
    .filter(
      (r) =>
        !r.deleted_at &&
        live.has(r.template_id) &&
        r.weekdays.includes(weekday) &&
        (!r.valid_from || r.valid_from <= date) &&
        (!r.valid_to || r.valid_to >= date),
    )
    .sort((a, b) => b.priority - a.priority)[0];
  if (rule) return rule.template_id;
  return templates.find((t) => t.is_fallback && live.has(t.id))?.id ?? null;
}

export interface BlockLike {
  id: string;
  title: string;
  slot_kind: SlotKind;
  start_local: string;
  end_local: string | null;
  group_title: string | null;
  is_optional: boolean;
  position: number;
}

export function sortBlocks<T extends BlockLike>(blocks: T[]): T[] {
  return [...blocks].sort((a, b) => timeToMinutes(a.start_local) - timeToMinutes(b.start_local) || a.position - b.position);
}

/** Blocks as shown on Vandaag: consecutive blocks with the same group title become one group (spec V3, L19). */
export type TimelineItem<T extends BlockLike> =
  { kind: "block"; block: T; start: number; end: number } | { kind: "group"; title: string; blocks: T[]; start: number; end: number };

/** End of a block in minutes: its own end, else the start of the next block, else the end of the day. */
export function blockEnds<T extends Pick<BlockLike, "start_local" | "end_local">>(sorted: T[]): number[] {
  return sorted.map((b, i) => {
    if (b.end_local) return timeToMinutes(b.end_local);
    const next = sorted[i + 1];
    return next ? timeToMinutes(next.start_local) : 24 * 60;
  });
}

export function buildTimeline<T extends BlockLike>(blocks: T[]): TimelineItem<T>[] {
  const sorted = sortBlocks(blocks);
  const ends = blockEnds(sorted);
  const items: TimelineItem<T>[] = [];
  sorted.forEach((block, i) => {
    const start = timeToMinutes(block.start_local);
    const end = ends[i] ?? start;
    const last = items[items.length - 1];
    if (block.group_title && last?.kind === "group" && last.title === block.group_title) {
      last.blocks.push(block);
      last.end = end;
    } else if (block.group_title) {
      items.push({ kind: "group", title: block.group_title, blocks: [block], start, end });
    } else {
      items.push({ kind: "block", block, start, end });
    }
  });
  return items;
}

/** Index of the item that is running at the given minute of the planning day (or the last one that started). */
export function currentIndex<T extends BlockLike>(items: TimelineItem<T>[], minute: number): number {
  let index = -1;
  items.forEach((item, i) => {
    if (item.start <= minute) index = i;
  });
  return index;
}

/**
 * A block counts in "x van y klaar" unless it is work (never checkable), optional and not done (spec V4, L33), or
 * skipped on purpose ("Ik loop achter", L45).
 */
export function countsTowardsProgress(block: { slot_kind: SlotKind; is_optional: boolean; status: string }): boolean {
  if (block.slot_kind === "work" || block.status === "skipped") return false;
  if (block.is_optional) return block.status === "done";
  return true;
}

export function isCheckable(block: { slot_kind: SlotKind }): boolean {
  return block.slot_kind !== "work";
}

/** Does the day type of a date pause routines (Ziek, Vakantie)? For counters and "Nog deze week" (R3, R4). */
export function pausedDays(
  templates: (TemplateLike & { pauses_routines: boolean })[],
  rules: RuleLike[],
  overrides: OverrideLike[],
): (date: string) => boolean {
  const pausing = new Set(templates.filter((t) => t.pauses_routines).map((t) => t.id));
  return (date) => {
    const id = pickTemplateId(date, templates, rules, overrides);
    return !!id && pausing.has(id);
  };
}
